import { useMemo } from "react"
import { numberToCurrency } from "helpers/string"

const useSummaryDetails = (paymentSummary, customFees = [], { combineFees = false, mergeAllFeesToTax = false, showSubtotalAdjustment = true } = {}) => {
  const {
    tax_and_fee,
    adult,
    child,
    infant,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate,
    custom_fees: paymentCustomFees,
    discount_amount,
    discount_code,
    subtotal: paymentSubtotal,
    non_line_item_fees_total,
    subtotal_adjusted,
    original_subtotal,
    adjusted_subtotal,
    subtotal_adjustment_reason
  } = paymentSummary ?? {}

  return useMemo(() => {
    let subtotal = 0

    const details = []

    const addItem = (labelKey, item) => {
      if (item?.count && item?.price) {
        details.push({ labelKey, count: item.count, value: item.price })
        subtotal += item.price ?? 0
      }
    }
    // When the subtotal was negotiated and we're hiding the adjustment (guest view), suppress the
    // per-guest line items too — the guest only sees the final subtotal, not how it was derived.
    const hideGuestBreakdown = subtotal_adjusted && !showSubtotalAdjustment
    if (!hideGuestBreakdown) {
      if (pricing_type === "charge_per_person") {
        const items = [
          { labelKey: "client.payment.labels.adult_x", item: adult },
          { labelKey: "client.payment.labels.child_x", item: child },
          { labelKey: "client.payment.labels.infant_x", item: infant },
          { labelKey: "client.payment.labels.passenger_x", item: passenger }
        ]
        for (const { labelKey, item } of items) addItem(labelKey, item)
      } else if (pricing_type === "charge_flat_rate") {
        details.push({ labelKey: "client.payment.labels.flat_rate_for_included_guests", value: flat_rate ?? 0 })
        subtotal += flat_rate ?? 0
        const extraItems = [
          { labelKey: "client.payment.labels.extra_adult_x", item: extra_adult },
          { labelKey: "client.payment.labels.extra_child_x", item: extra_child },
          { labelKey: "client.payment.labels.extra_infant_x", item: extra_infant },
          { labelKey: "client.payment.labels.extra_passenger_x", item: extra_passenger }
        ]
        for (const { labelKey, item } of extraItems) addItem(labelKey, item)
      }
    }

    if (subtotal_adjusted && showSubtotalAdjustment) {
      // Negotiated subtotal: show the net subtotal (negotiated base + add-ons) on the row, strike
      // the original computed subtotal, and surface the negotiated base + reason via the caption.
      details.push({
        labelKey: "client.payment.labels.subtotal",
        value: paymentSubtotal ?? subtotal,
        fw: 600,
        isSubtotal: true,
        isSubtotalAdjusted: true,
        originalSubtotal: original_subtotal,
        adjustedPrice: adjusted_subtotal,
        adjustmentReason: subtotal_adjustment_reason
      })
    } else {
      details.push({ labelKey: "client.payment.labels.subtotal", value: paymentSubtotal ?? subtotal, fw: 600, isSubtotal: true })
    }

    if (discount_amount > 0) {
      const discountLabel = discount_code ? `Discount (${discount_code})` : "Discount"
      details.push({ label: discountLabel, value: -discount_amount, isDiscount: true })
    }

    const feesToUse = paymentCustomFees ?? customFees

    if (combineFees) {
      const cardFeeEntry = feesToUse.find((f) => f.title === "Card Processing Fee")
      const regularFees = feesToUse.filter((f) => f.title !== "Card Processing Fee")
      const cardFeeAmount = cardFeeEntry ? Number(cardFeeEntry.price) : 0

      if (mergeAllFeesToTax) {
        // Guide detail page: roll every fee (line-item or not, including the card
        // processing fee) plus the platform fee into one collapsible "Tax & Fees" row,
        // with the breakdown shown via `info`.
        const feeInfo = [...regularFees, ...(cardFeeEntry ? [cardFeeEntry] : [])]
          ?.filter((f) => Number(f.price) > 0)
          ?.map((f) => ({ label: f.title, amount: numberToCurrency(Number(f.price)) }))
        // tax_and_fee is the platform fee. On modern bookings it already appears inside
        // custom_fees as a "Platform Fee" entry; only add it here for legacy bookings that
        // omit it, otherwise it would be counted twice.
        const hasPlatformFee = regularFees.some((f) => f.title === "Platform Fee")
        const platformFeeAmount = hasPlatformFee ? 0 : (tax_and_fee ?? 0)
        if (platformFeeAmount > 0) {
          feeInfo.push({ label: "Platform Fee", amount: numberToCurrency(platformFeeAmount) })
        }
        const taxAndFeesValue = regularFees.reduce((sum, f) => sum + Number(f.price), 0) + cardFeeAmount + platformFeeAmount
        if (taxAndFeesValue > 0) {
          details.push({ label: "Tax & Fees", value: taxAndFeesValue, combinedFee: true, info: feeInfo })
        }
      } else {
        for (const fee of regularFees) {
          if (fee.show_as_line_item !== false) {
            details.push({ label: fee.title, value: Number(fee.price) })
          }
        }

        // The card processing fee is shown as its own line only when show_as_line_item !== false;
        // otherwise it's folded into the combined "Tax & Fees" row (matching every other fee).
        const showCardFeeAsLine = !!cardFeeEntry && cardFeeEntry.show_as_line_item !== false

        const taxAndFeesValue = non_line_item_fees_total != null
          ? non_line_item_fees_total
          : regularFees.filter((f) => f.show_as_line_item === false).reduce((sum, f) => sum + Number(f.price), 0)
            + (showCardFeeAsLine ? 0 : cardFeeAmount)
            + (tax_and_fee ?? 0)
        if (taxAndFeesValue > 0) {
          details.push({ label: "Tax & Fees", value: taxAndFeesValue, combinedFee: true })
        }

        if (showCardFeeAsLine && cardFeeAmount > 0) {
          details.push({ label: "Card Processing Fee", value: cardFeeAmount, isCardFee: true })
        }
      }
    } else {
      for (const fee of feesToUse) {
        details.push({ label: fee.title, value: Number(fee.price) })
      }
      details.push({ labelKey: "client.payment.labels.taxes_fees", value: tax_and_fee ?? 0 })
    }

    return details
  }, [
    adult,
    child,
    infant,
    passenger,
    tax_and_fee,
    flat_rate,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    pricing_type,
    paymentCustomFees,
    customFees,
    combineFees,
    mergeAllFeesToTax,
    showSubtotalAdjustment,
    discount_amount,
    discount_code,
    paymentSubtotal,
    non_line_item_fees_total,
    subtotal_adjusted,
    original_subtotal,
    adjusted_subtotal,
    subtotal_adjustment_reason
  ])
}

export default useSummaryDetails
